import { useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ArtisticStandard() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: bodyRef, isVisible: bodyVisible } = useScrollAnimation();
  const { ref: colsRef, isVisible: colsVisible } = useScrollAnimation();
  const { ref: closingRef, isVisible: closingVisible } = useScrollAnimation();

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ backgroundColor: '#1a1a2e' }}>
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #d4a843 0%, transparent 50%), radial-gradient(circle at 80% 50%, #d4a843 0%, transparent 50%)' }}
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-12 relative">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 animate-fade-up ${headerVisible ? 'in-view' : ''}`}
        >
          <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6 leading-tight">
            Carnegie Hall Caliber. In the Comfort of Your Home.
          </h2>
        </div>

        {/* Body Paragraph */}
        <div
          ref={bodyRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 animate-fade-up delay-100 ${bodyVisible ? 'in-view' : ''}`}
        >
          <p className="text-lg text-gray-200 leading-relaxed max-w-3xl mx-auto" style={{ lineHeight: '1.8' }}>
            The Salon Society maintains a singular, uncompromising benchmark: every artist we present is conservatory trained at the highest level.
          </p>
        </div>

        {/* Two Column Layout */}
        <div
          ref={colsRef as React.RefObject<HTMLDivElement>}
          className={`grid md:grid-cols-2 gap-12 lg:gap-16 mb-20 animate-fade-up delay-200 ${colsVisible ? 'in-view' : ''}`}
        >
          <div>
            <h3 className="font-serif text-2xl text-amber-400 mb-8 text-center md:text-left">
              Premier Institutions
            </h3>
            <ul className="space-y-4">
              {[
                'The Juilliard School',
                'Colburn School',
                'USC Thornton School of Music',
                'Hopscotch Opera',
                'LA Opera Buffs',
                'Aspen Music Festival and School',
              ].map((institution) => (
                <li key={institution} className="flex items-start gap-3 text-gray-200">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                  <span className="text-base" style={{ lineHeight: '1.8' }}>{institution}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-serif text-2xl text-amber-400 mb-8 text-center md:text-left">
              Distinguished Stages
            </h3>
            <ul className="space-y-4">
              {[
                'Carnegie Hall',
                'Alice Tully Hall',
                'David Geffen Hall',
                'Walt Disney Concert Hall',
                'New World Center',
                'John F. Kennedy Center for the Performing Arts',
                'Aspen Music Festival and School',
                'Wheeler Opera House',
                'Schermerhorn Symphony Center',
                'American Museum of Natural History',
              ].map((stage) => (
                <li key={stage} className="flex items-start gap-3 text-gray-200">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                  <span className="text-base" style={{ lineHeight: '1.8' }}>{stage}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="w-24 h-px bg-amber-600 mx-auto mb-12" />

        {/* Closing Statement */}
        <div
          ref={closingRef as React.RefObject<HTMLDivElement>}
          className={`text-center animate-fade-up delay-300 ${closingVisible ? 'in-view' : ''}`}
        >
          <p className="font-serif text-xl lg:text-2xl text-gray-100 leading-relaxed max-w-3xl mx-auto" style={{ lineHeight: '1.8' }}>
            These are artists accustomed to the world's most respected stages — now performing within the intimacy of your home.
          </p>
        </div>

      </div>
    </section>
  );
}
